// The Trades page by day or by week: one row per trading day (or week) with its totals; opened,
// it shows the P&L through the day, the key numbers and every trade.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDuration, formatMoney, holdMs, netPnl, tradingDayKey, usd } from './pnl';
import { showResult, tradeMoves } from './display';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { GradeBadge } from './grades';
import { TagChip, tagKind } from './tags';
import { addDays, weekOf } from './weeklyGrades';
import DayInsightsCard, { DayInsightBadge } from './DayInsightsCard';
import { dayInsights } from './dayInsights';
import { LinkBar, LinkChip, linkGroups, useTradeSelection } from './LinkedTrades';

const PAGE = 10;
const pnlClass = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const gross = (t) => netPnl(t) + (t.fees || 0);
const pf = (g) => (g.pf === null ? '—' : g.pf === Infinity ? '∞' : g.pf.toFixed(2));

function summarize(key, trades) {
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = sorted.map(netPnl);
  const won = nets.filter((n) => n > 0);
  const lost = nets.filter((n) => n < 0);
  const winSum = won.reduce((a, b) => a + b, 0);
  const lossSum = -lost.reduce((a, b) => a + b, 0);
  let run = 0;
  const series = [0, ...nets.map((n) => (run += n))];
  return {
    key, trades: sorted, series,
    net: run, gross: sorted.reduce((s, t) => s + gross(t), 0), fees: sorted.reduce((s, t) => s + (t.fees || 0), 0),
    wins: won.length, losses: lost.length, contracts: sorted.reduce((s, t) => s + (t.size || 1), 0),
    winRate: sorted.length ? (won.length / sorted.length) * 100 : 0,
    pf: lossSum > 0 ? winSum / lossSum : winSum > 0 ? Infinity : null,
    best: nets.length ? Math.max(...nets) : 0, worst: nets.length ? Math.min(...nets) : 0,
  };
}

// The running P&L through the day: green above zero, red below, with the trade times underneath.
function DayChart({ g, mode, id }) {
  const series = g.series;
  const W = 600;
  const H = 200;
  const pad = { l: 58, r: 10, t: 10, b: 26 };
  const max = Math.max(0, ...series);
  const min = Math.min(0, ...series);
  const span = max - min || 1;
  const x = (i) => pad.l + (i / Math.max(1, series.length - 1)) * (W - pad.l - pad.r);
  const y = (v) => pad.t + ((max - v) / span) * (H - pad.t - pad.b);
  const zero = y(0);
  const line = series.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(series.length - 1).toFixed(1)},${zero.toFixed(1)} L${x(0).toFixed(1)},${zero.toFixed(1)} Z`;
  const ticks = [...new Set([max, max / 2, 0, min / 2, min].map((v) => Math.round(v)))];
  const label = (tr) => (mode === 'all' ? new Date(tr.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
    : tr.timeUnknown ? '' : new Date(tr.date).toLocaleString(currentLocale(), mode === 'week' ? { weekday: 'short' } : { hour: 'numeric', minute: '2-digit' }));
  const n = g.trades.length;
  const marks = [...new Set([0, Math.floor((n - 1) / 2), n - 1])].map((i) => ({ i: i + 1, text: label(g.trades[i]) }));
  return (
    <svg className="tday-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="P&L through the day">
      <defs>
        <clipPath id={`up-${id}`}><rect x="0" y="0" width={W} height={zero} /></clipPath>
        <clipPath id={`down-${id}`}><rect x="0" y={zero} width={W} height={H - zero} /></clipPath>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2fe39a" stopOpacity="0.6" /><stop offset="100%" stopColor="#2fe39a" stopOpacity="0.04" />
        </linearGradient>
        <linearGradient id={`r-${id}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#ff5d73" stopOpacity="0.6" /><stop offset="100%" stopColor="#ff5d73" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className={v === 0 ? 'tday-zero' : 'tday-grid'} />
          <text x={pad.l - 8} y={y(v) + 4} className="tday-label" textAnchor="end">{usd.format(v).replace('.00', '')}</text>
        </g>
      ))}
      {marks.map((m, k) => m.text && (
        <text key={m.i} x={x(m.i)} y={H - 6} className="tday-label" textAnchor={marks.length > 1 && k === marks.length - 1 ? 'end' : m.i === 1 && marks.length > 1 ? 'start' : 'middle'}>{m.text}</text>
      ))}
      <path d={area} fill={`url(#g-${id})`} clipPath={`url(#up-${id})`} />
      <path d={area} fill={`url(#r-${id})`} clipPath={`url(#down-${id})`} />
      <path d={line} className="tday-line" fill="none" />
      {series.slice(1).map((v, i) => <circle key={i} cx={x(i + 1)} cy={y(v)} r="3" className={`tday-dot ${pnlClass(v - series[i])}`} />)}
    </svg>
  );
}

function Tile({ label, value, sub, className = '' }) {
  return (
    <div className="tday-tile">
      <span className={`tday-tile-value ${className}`}>{value}</span>
      <span className="tday-tile-label">{label}</span>
      {sub && <span className="tday-tile-sub">{sub}</span>}
    </div>
  );
}

function DayRow({ g, mode, open, onToggle, notes, library, showAccount, accountLabel, onOpenTrade, size, insight, insightsSetup }) {
  const { t } = useI18n();
  const shown = (list, net) => showResult({ net, moves: tradeMoves(list), size, money: formatMoney, t });
  const netShown = shown(g.trades, g.net);
  const fmt = (key, o) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), o);
  const title = mode === 'week'
    ? t('Week of {dates}', { dates: `${fmt(addDays(g.key, 1), { month: 'short', day: 'numeric' })} – ${fmt(addDays(g.key, 5), { month: 'short', day: 'numeric' })}` })
    : fmt(g.key, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const note = mode === 'day' && notes[g.key];
  const hasNote = Boolean(note && ((note.review && note.review.trim()) || (note.plan && note.plan.trim()) || note.score));
  const picking = useTradeSelection(g.trades);
  const groups = useMemo(() => linkGroups(g.trades), [g.trades]);
  return (
    <li className={`tday${open ? ' open' : ''}`} id={`day-${g.key}`}>
      <button type="button" className="tday-row" onClick={onToggle} aria-expanded={open}>
        <span className="tday-date">{title}{insight && <DayInsightBadge entry={insight} />}</span>
        <span className={`tday-num ${netShown.tone}`}>{netShown.text}</span>
        <span className="tday-num">{g.trades.length}</span>
        <span className="tday-num tday-wide">{Math.round(g.winRate)}%</span>
        <span className={`tday-num tday-wide ${pnlClass(g.gross)}`}>{formatMoney(g.gross)}</span>
        <span className="tday-num tday-wide">{usd.format(g.fees)}</span>
        <span className={`tday-num tday-wide ${g.pf === null ? '' : g.pf >= 1 ? 'gain' : 'loss'}`}>{pf(g)}</span>
        <span className={`tday-chevron${open ? ' open' : ''}`} aria-hidden="true">&#8964;</span>
      </button>
      {open && (
        <div className="tday-panel">
          {insight && <DayInsightsCard entry={insight} preview={insightsSetup.preview} canAskMark={insightsSetup.mark} date={fmt(g.key, { weekday: 'long', month: 'long', day: 'numeric' })} />}
          <div className="tday-top">
            <div className="tday-chart-box">
              <DayChart g={g} mode={mode} id={g.key} />
            </div>
            <div className="tday-tiles">
              <Tile label={t('Trades')} value={g.trades.length} sub={<><span className="gain">{g.wins} {t('won')}</span> · <span className="loss">{g.losses} {t('lost')}</span></>} />
              <Tile label={t('Net P&L')} value={netShown.text} className={netShown.tone} sub={`${t('Gross')} ${formatMoney(g.gross)}`} />
              <Tile label={t('Win rate')} value={`${Math.round(g.winRate)}%`} sub={t('{n} contracts', { n: g.contracts })} />
              <Tile label={t('Profit factor')} value={pf(g)} className={g.pf === null ? '' : g.pf >= 1 ? 'gain' : 'loss'} />
              <Tile label={t('Commissions & fees')} value={usd.format(g.fees)} />
              <Tile label={t('Best / worst trade')} value={<><span className={pnlClass(g.best)}>{formatMoney(g.best)}</span><span className="tday-slash"> / </span><span className={pnlClass(g.worst)}>{formatMoney(g.worst)}</span></>} />
            </div>
          </div>
          <LinkBar trades={g.trades} selected={picking.selected} onClear={picking.clear} library={library} />
          <div className="table-wrap tday-trades">
            <table>
              <thead>
                <tr>
                  <th className="lt-th"><input type="checkbox" aria-label={t('Select all')} checked={picking.selected.size > 0 && picking.selected.size === g.trades.length} onChange={picking.all} /></th>
                  <th>{mode === 'week' ? t('Date') : t('Time')}</th>
                  {showAccount && <th>{t('Account')}</th>}
                  <th>{t('Symbol')}</th>
                  <th>{t('Side')}</th>
                  <th>{t('Size')}</th>
                  <th>{t('Entry')}</th>
                  <th>{t('Exit')}</th>
                  <th>{t('Net P&L')}</th>
                  <th>{t('Duration')}</th>
                  <th>{t('Grade')}</th>
                  <th>{t('Tags')}</th>
                </tr>
              </thead>
              <tbody>
                {g.trades.map((tr) => {
                  const net = netPnl(tr);
                  const when = new Date(tr.date);
                  const hold = holdMs(tr);
                  return (
                    <tr key={tr._id} className={`trade-row${picking.selected.has(tr._id) ? ' picked' : ''}${tr.linkId && groups[tr.linkId] ? ' linked' : ''}`} tabIndex={0} title={t('Open this trade')} onClick={() => onOpenTrade(tr)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onOpenTrade(tr); }} style={tr.linkId && groups[tr.linkId] ? { '--lt': groups[tr.linkId].color } : undefined}>
                      <td className="lt-td" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={picking.selected.has(tr._id)} onChange={() => picking.toggle(tr._id)} aria-label={t('Select this trade')} /></td>
                      <td>{tr.timeUnknown ? '—' : mode === 'week'
                        ? when.toLocaleString(currentLocale(), { weekday: 'short', hour: 'numeric', minute: '2-digit' })
                        : when.toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</td>
                      {showAccount && <td className="account-cell">{accountLabel(tr.accountId)}</td>}
                      <td className="symbol">{tr.symbol}</td>
                      <td className={(tr.side || 'Long') === 'Short' ? 'loss' : 'gain'}>{t(tr.side || 'Long')}</td>
                      <td>{tr.size || 1}</td>
                      <td>{Number(tr.entryPrice).toFixed(2)}</td>
                      <td>{Number(tr.exitPrice).toFixed(2)}</td>
                      <td className={pnlClass(net)}>{shown([tr], net).text}</td>
                      <td className="duration-cell">{hold === null ? '—' : formatDuration(hold)}</td>
                      <td>{tr.grade ? <GradeBadge grade={tr.grade} /> : <span className="sidebar-muted">—</span>}</td>
                      <td className="tags-cell"><LinkChip trade={tr} groups={groups} onPick={picking.pickGroup} />{(tr.tags || []).map((name) => <TagChip key={name} name={name} kind={tagKind(name, library)} />)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {mode === 'day' && (
            <div className="tday-foot">
              <Link to={`/app/reflection?date=${g.key}`} className="tday-note">
                {hasNote ? `📝 ${t('View note')}` : `+ ${t('Add note')}`}
              </Link>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// Above the "All trades" table: the same chart and number tiles as a day, for every trade shown.
export function AllTradesSummary({ trades, size }) {
  const { t } = useI18n();
  if (!trades.length) return null;
  const g = summarize('all', trades);
  const netShown = showResult({ net: g.net, moves: tradeMoves(g.trades), size, money: formatMoney, t });
  return (
    <div className="tday-top all-trades-summary">
      <div className="tday-chart-box">
        <DayChart g={g} mode="all" id="all" />
      </div>
      <div className="tday-tiles">
        <Tile label={t('Trades')} value={g.trades.length} sub={<><span className="gain">{g.wins} {t('won')}</span> · <span className="loss">{g.losses} {t('lost')}</span></>} />
        <Tile label={t('Net P&L')} value={netShown.text} className={netShown.tone} sub={`${t('Gross')} ${formatMoney(g.gross)}`} />
        <Tile label={t('Win rate')} value={`${Math.round(g.winRate)}%`} sub={t('{n} contracts', { n: g.contracts })} />
        <Tile label={t('Profit factor')} value={pf(g)} className={g.pf === null ? '' : g.pf >= 1 ? 'gain' : 'loss'} />
        <Tile label={t('Commissions & fees')} value={usd.format(g.fees)} />
        <Tile label={t('Best / worst trade')} value={<><span className={pnlClass(g.best)}>{formatMoney(g.best)}</span><span className="tday-slash"> / </span><span className={pnlClass(g.worst)}>{formatMoney(g.worst)}</span></>} />
      </div>
    </div>
  );
}

// insights: { preview, mark, accounts } to show Day insights on each day (by-day view only).
export default function TradeDays({ trades, mode, notes, library, showAccount, accountLabel, onOpenTrade, size, insights = null }) {
  const { t } = useI18n();
  const [limit, setLimit] = useState(PAGE);
  const [opened, setOpened] = useState(() => new Set());
  const groups = useMemo(() => {
    const map = new Map();
    for (const tr of trades) {
      const day = tradingDayKey(tr.date);
      const key = mode === 'week' ? weekOf(day) : day;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(tr);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([k, list]) => summarize(k, list));
  }, [trades, mode]);

  const insightsByDay = useMemo(() => (insights && mode === 'day' ? dayInsights(trades, { accounts: insights.accounts || [], notes }) : null), [insights, mode, trades, notes]);

  // The newest day (or week) starts open.
  useEffect(() => { setOpened(new Set(groups[0] ? [groups[0].key] : [])); setLimit(PAGE); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (key) => setOpened((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  if (!groups.length) return <p className="empty">{t('No trades match.')}</p>;
  return (
    <div className="tdays">
      <div className="tday-head" aria-hidden="true">
        <span>{mode === 'week' ? t('Week') : t('Date')}</span>
        <span className="tday-num">{t('Net P&L')}</span>
        <span className="tday-num">{t('Trades')}</span>
        <span className="tday-num tday-wide">{t('Win rate')}</span>
        <span className="tday-num tday-wide">{t('Gross P&L')}</span>
        <span className="tday-num tday-wide">{t('Fees')}</span>
        <span className="tday-num tday-wide">{t('Profit factor')}</span>
        <span />
      </div>
      <ul className="tday-list">
        {groups.slice(0, limit).map((g) => (
          <DayRow key={g.key} g={g} mode={mode} open={opened.has(g.key)} onToggle={() => toggle(g.key)} notes={notes} library={library}
            showAccount={showAccount} accountLabel={accountLabel} onOpenTrade={onOpenTrade} size={size}
            insight={insightsByDay && insightsByDay[g.key]} insightsSetup={insights} />
        ))}
      </ul>
      {groups.length > limit && (
        <div className="tday-more">
          <button type="button" className="secondary" onClick={() => setLimit((n) => n + PAGE)}>
            {mode === 'week' ? t('Show {n} more weeks', { n: Math.min(PAGE, groups.length - limit) }) : t('Show {n} more days', { n: Math.min(PAGE, groups.length - limit) })}
          </button>
        </div>
      )}
    </div>
  );
}
